import { useState } from 'react';
import LessonStatusIcon from './LessonStatusIcon';
import { useStorageDownloadUrl } from '../hooks/useStorageDownloadUrl';
import { formatLessonDate } from '../utils/lessonDate';
import { BTN_PRIMARY_SM } from './buttonStyles';
import { firstFramePath, SLOT_LABEL, type ProgramSlot } from '../utils/programSlots';

/**
 * One row per lesson slot, shared by the player's program screen and the
 * coach's. The two differ only in what a row can DO — the player uploads, the
 * coach records — so the control is passed in and the recipe is not copied.
 */

/**
 * Absent thumbnails are the normal case here, not the exception: an upcoming
 * slot has no lesson at all, a reserved one has no video, and anything
 * uploaded before the preprocessor has no strip. So the empty state is a
 * designed slot rather than a fallback — the neutral fill §8.3 uses for
 * secondary surfaces, with a film glyph at the same weight as secondary text.
 * Never the coach's avatar: the coach is named once, at the top.
 */
function SlotThumb({ path }: { path: string | null }) {
  const url = useStorageDownloadUrl(path);
  const [broken, setBroken] = useState(false);

  if (url && !broken) {
    return (
      <img
        src={url}
        alt=""
        onError={() => setBroken(true)}
        className="w-11 h-11 shrink-0 object-cover bg-black"
        style={{ borderRadius: 8 }}
      />
    );
  }
  return (
    <div
      className="w-11 h-11 shrink-0 flex items-center justify-center"
      style={{ borderRadius: 8, background: 'rgba(120,120,128,0.16)' }}
      aria-hidden="true"
    >
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none"
        stroke="rgba(60,60,67,0.6)" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <rect x="2" y="5" width="14" height="14" rx="2" />
        <path d="M16 10l6-3v10l-6-3z" />
      </svg>
    </div>
  );
}

export function SlotRow({
  slot, first, label: labelOverride, onOpen, action,
}: {
  slot: ProgramSlot;
  first: boolean;
  /** Overrides the status label, so the coach's side can name states its way. */
  label?: string;
  /** Undefined when the row has nothing to open. */
  onOpen?: () => void;
  /** The row's own control: Upload for a player, Start Lesson for a coach. */
  action?: { label: string; onClick: () => void; disabled?: boolean; testId?: string };
}) {
  const { lesson, state } = slot;
  const label = labelOverride ?? (state === 'uploading' && slot.progress != null
    ? `Uploading ${Math.round(slot.progress)}%`
    : SLOT_LABEL[state]);

  /* One fact per line. The title says which lesson, the status says where it
     is, and the meta says when — the date that matters differs by state, so
     only one is ever shown. */
  const meta = state === 'ready_to_watch' || state === 'complete'
    ? (lesson?.deliveredAt ? `Delivered ${formatLessonDate(lesson.deliveredAt)}` : null)
    : state === 'awaiting_review'
      ? (lesson?.updatedAt ? `Submitted ${formatLessonDate(lesson.updatedAt)}` : null)
      : null;

  const dimmed = state === 'upcoming' || state === 'refunded';

  const body = (
    <>
      <SlotThumb path={firstFramePath(lesson)} />
      <div className="min-w-0 flex-1 flex flex-col gap-0.5 text-left">
        <span className={`text-[17px] leading-[22px] tracking-[-0.43px] ${dimmed ? 'text-[rgba(60,60,67,0.6)]' : 'text-[#1C1C1C]'}`}>
          Lesson {slot.number}
        </span>
        {/* Nothing truncates because the screen is narrow, and
                nothing is dropped either. It reflows: below md the date takes
                its own line under the status.
            It used to hide below md, which lost the one fact that says how long
            something has been waiting. The separator goes with the wrap; a "·"
            leading a line is an orphan. */}
        <span className="flex flex-wrap items-center gap-x-[6px] text-[13px] leading-[18px] min-w-0">
          <span className="flex items-center gap-[6px] min-w-0">
            <LessonStatusIcon label={label} />
            <span className="shrink-0 font-medium text-[#1C1C1C]">{label}</span>
          </span>
          {meta && (
            <span className="truncate font-normal text-[rgba(60,60,67,0.6)] basis-full pl-5 md:basis-auto md:pl-0">
              <span className="hidden md:inline">· </span>{meta}
            </span>
          )}
        </span>
      </div>
    </>
  );

  /* A row that opens something is itself the control, with the chevron §8 gives
     a row that discloses — the delivered ones and the one in play. The word
     "Watch" is not printed beside it: the status line already says "Ready to
     watch", and the row's job is not a third statement of the same fact.
     Upcoming and refunded rows stay inert: no chevron, no hover, nothing that
     promises a screen that does not exist. */
  if (onOpen) {
    return (
      <div className="pl-4">
        <div className={first ? '' : 'border-t border-[#e6e6e6]'}>
          <button
            type="button"
            onClick={onOpen}
            className="ios-press w-full flex items-center gap-3 pr-4 py-3 text-left cursor-pointer hover:bg-[rgba(120,120,128,0.08)] transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-db-accent/40"
          >
            {body}
            <svg className="shrink-0 text-[rgba(60,60,67,0.3)]" width="16" height="16" viewBox="0 0 24 24"
              fill="none" stroke="currentColor" strokeWidth="2.5" aria-hidden="true">
              <polyline points="9 6 15 12 9 18" />
            </svg>
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="pl-4">
      <div className={first ? '' : 'border-t border-[#e6e6e6]'}>
        <div className="flex items-center gap-3 pr-4 py-3">
          {body}
          {action && (
            <button
              type="button"
              onClick={action.onClick}
              disabled={action.disabled}
              data-testid={action.testId}
              className={`${BTN_PRIMARY_SM} shrink-0 disabled:opacity-40 disabled:cursor-not-allowed`}
            >
              {action.label}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
